import { cn } from "@/lib/utils"
import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

export type TimelineTone = "success" | "warning" | "danger" | "yellow" | "muted"

export interface TimelineItem {
  id: string
  icon: LucideIcon
  tone?: TimelineTone
  title: string
  detail?: ReactNode
  time?: string // right-aligned mono label (e.g. "13.05." or "08:14")
  tag?: string // dim inline tag after the title (e.g. "scheduled")
  dim?: boolean // future / not-yet-happened step — render the whole row muted
}

const TONE_RING: Record<TimelineTone, string> = {
  success: "border-success text-success",
  warning: "border-warning text-warning",
  danger: "border-red1 text-red1",
  yellow: "border-yellow1 text-yellow1",
  muted: "border-grey4 text-grey5",
}

// Softer rings — used where the timeline is a quiet, secondary element.
const TONE_RING_SUBTLE: Record<TimelineTone, string> = {
  success: "border-success/50 text-success/70",
  warning: "border-warning/50 text-warning/70",
  danger: "border-red1/50 text-red1/70",
  yellow: "border-yellow1/50 text-yellow1/70",
  muted: "border-grey4 text-grey5",
}

/** Vertical event timeline shared across the inquiry detail and template-settings views. */
export function Timeline({
  items,
  className,
  subtle = false,
}: {
  items: TimelineItem[]
  className?: string
  subtle?: boolean // dim the ring/icon colors for a quieter presentation
}) {
  const ringTones = subtle ? TONE_RING_SUBTLE : TONE_RING
  return (
    <ol className={className}>
      {items.map((item, i) => {
        const Icon = item.icon
        const last = i === items.length - 1
        return (
          <li
            key={item.id}
            className={cn("flex gap-3", item.dim && "opacity-50")}
          >
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 bg-grey2",
                  ringTones[item.tone ?? "muted"],
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              {!last && <span className="my-1 w-px flex-1 bg-grey4" />}
            </div>
            <div className="min-w-0 flex-1 pb-5 pt-1.5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-sm font-medium text-white">
                    {item.title}
                  </span>
                  {item.tag && (
                    <span className="text-[11px] text-grey5">· {item.tag}</span>
                  )}
                </div>
                {item.time && (
                  <span className="font-mono shrink-0 text-[12px] text-grey5">
                    {item.time}
                  </span>
                )}
              </div>
              {item.detail && (
                <div className="mt-0.5 text-[12.5px] leading-relaxed text-grey5">
                  {item.detail}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
